import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { formatPhoneNumber } from 'utils';
import { Select, SelectOption } from '../Select';

export type StatusUpdateType = 'email' | 'text' | 'email&text' | 'office';
type DefaultMethodType = 'email' | 'text' | 'office';
type SelectedUserType = {
  email: string;
  phone?: string;
  officePhone?: string;
  bookShowingDefault?: {
    email: boolean;
    text: boolean;
    office: boolean;
  };
};
export interface ContactMethodsSelectorProps {
  selectedUser: SelectedUserType;
  selectNotifyMethod: (option: StatusUpdateType) => void;
  hasSmsFeature?: boolean;
}

const ContactMethodsSelector: React.FC<ContactMethodsSelectorProps> = ({
  selectedUser,
  selectNotifyMethod,
  hasSmsFeature,
}) => {
  const { phone, email, officePhone, bookShowingDefault } = selectedUser;

  const defaultValue = useMemo(() => {
    if (bookShowingDefault) {
      const trueKeys = Object.keys(bookShowingDefault).filter(
        (key: DefaultMethodType) => bookShowingDefault[key],
      );
      if (!(trueKeys.includes('text') && !hasSmsFeature)) {
        return trueKeys.length > 1 ? 'email&text' : trueKeys[0];
      }
    }
    return 'email';
  }, [bookShowingDefault, hasSmsFeature]);

  useEffect(() => {
    selectNotifyMethod(defaultValue as StatusUpdateType);
  }, [defaultValue]);

  return (
    <StyledSelect
      key={email}
      onChange={value => {
        selectNotifyMethod(value);
      }}
      defaultValue={defaultValue}
      getPopupContainer={triggerNode => triggerNode}
      data-cy="contact-methods-selector-container"
    >
      <SelectOption
        value="email"
        data-cy="notify-by-email"
      >{`Email: ${email}`}</SelectOption>
      {hasSmsFeature && phone && (
        <SelectOption
          value="text"
          data-cy="notify-by-text"
        >{`SMS: ${formatPhoneNumber(phone)}`}</SelectOption>
      )}
      {hasSmsFeature && email && phone && (
        <SelectOption value="email&text" data-cy="notify-by-email-and-text">
          Email & SMS
        </SelectOption>
      )}
      {officePhone && (
        <SelectOption
          value="office"
          data-cy="notify-by-office"
        >{`Office: ${formatPhoneNumber(officePhone)}`}</SelectOption>
      )}
    </StyledSelect>
  );
};

const StyledSelect = styled(Select)`
  margin-bottom: 8px;
  .ant-select > .ant-select-selector {
    background-color: #fff;
    border: 1px solid #e4e5e7;
  }
`;

export default ContactMethodsSelector;
